import { _ as _extends } from '../../_rollupPluginBabelHelpers-mO86zOh3.js';
import { forwardRef, useRef, useMemo, useEffect, useCallback } from 'react';
import useId from '@indeed/ihl-use-id';
import { InteractiveListOption } from '../InteractiveList/InteractiveListOption.js';
import { useListboxContext } from './useListboxContext.js';
import { mergeRefs } from '../../utils/mergeRefs.js';
import { jsx } from '@emotion/react';
import '../InteractiveList/InteractiveListBaseItem.js';
import '@indeed/ifl-css';
import '../../utils/transition.js';
import '@indeed/ifl-icons';
import '../Text/Text.js';
import '../../utils/createResponsive.js';
import '../Flex/Flex.js';
import '../Box/Box.js';

const Option = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    value,
    contentAfter,
    contentBefore,
    disabled = false,
    description,
    id,
    text,
    ...remainingProps
  } = props;
  const {
    selectionType,
    activeOption,
    setActiveOption,
    selectOption,
    selectedOptionValues,
    registerOption,
    setFocusVisible,
    focusVisible
  } = useListboxContext();
  const optionId = useId(id);
  const optionRef = useRef(null);
  const optionText = getOptionText(text, children);
  const optionValue = value ?? optionText;
  const optionData = useMemo(() => ({
    id: optionId,
    ref: optionRef,
    text: optionText,
    value: optionValue,
    disabled: !!disabled
  }), [optionId, optionRef, optionText, optionValue, disabled]);
  useEffect(() => {
    /**
     * Disabled options are treated similarly to native disabled `<option>`s in that
     * they cannot be interacted with. Instead of checking if an option is disabled in
     * various keydown handlers (e.g., arrowing through available options) and adding
     * custom logic (e.g., skipping over a disabled option), we simply avoid adding it
     * to the collection of options altogether.
     *
     * If this behavior should ever change (e.g., maybe disabled options should
     * receive virtual focus), register the option and hook into its disabled property
     * in the necessary event handlers.
     */
    if (!optionRef.current || disabled) return;
    return registerOption(optionData);
  }, [disabled, optionRef, optionData, registerOption]);
  const optionIsSelected = selectedOptionValues.some(selectedValue => selectedValue === optionValue) && !disabled;
  const handleOnClick = useCallback(event => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    setFocusVisible(false);
    setActiveOption(optionData);
    selectOption(optionData);
  }, [disabled, optionData, selectOption, setActiveOption, setFocusVisible]);
  return jsx(InteractiveListOption, _extends({
    as: "li",
    ref: mergeRefs(ref, optionRef),
    id: optionId,
    role: selectionType === "multiple" ? "menuitemcheckbox" : "option",
    "aria-selected": selectionType === "single" ? optionIsSelected : undefined,
    "aria-checked": selectionType === "multiple" ? optionIsSelected : undefined,
    "aria-disabled": disabled ? "true" : undefined,
    selected: optionIsSelected,
    focusVisible: focusVisible && activeOption?.id === optionId,
    selectionType: selectionType,
    contentAfter: contentAfter,
    contentBefore: contentBefore,
    supportiveContent: description,
    onClick: handleOnClick,
    onMouseDown: e => e.preventDefault() // Prevent option from taking focus away from parent on click
    ,
    sx: {
      scrollMargin: 4
    } // Ensure proper spacing from edge of container when navigating via keyboard
  }, remainingProps), children);
});
Option.displayName = "Option";
function getOptionText(text, children) {
  if (text !== undefined) {
    return text;
  }
  return typeof children === "string" ? children : "";
}

export { Option };
